import { useEffect, useState } from 'react';
import {
  chordQuestions,
  games,
  keyboardNotes,
  keySignatureQuestions,
  labelForNote,
  noteAudioPath,
  noteImagePath,
  pick,
  rhythmPatterns,
  shuffle,
  type GameId,
  type GameLevel,
  type GameMeta,
  type NoteName,
  type QuizQuestion,
  whiteNotes,
} from './gameData';

type Score = {
  total: number;
  correct: number;
  combo: number;
  bestCombo: number;
};

type ResultState = {
  ok: boolean;
  title: string;
  detail: string;
} | null;

const gameLevels: GameLevel[] = [1, 2, 3, 4];

type Surface = 'browser' | 'webview';

type MidiDevice = {
  id: string;
  rawId: string | number;
  name: string;
  type: string;
  inputPorts: number;
  outputPorts: number;
  isConnected: boolean;
};

type MidiMode = 'native' | 'webmidi' | 'unsupported';
type MidiStatus = 'idle' | 'ready' | 'connected' | 'unsupported' | 'error';

type MidiState = {
  mode: MidiMode;
  status: MidiStatus;
  devices: MidiDevice[];
  selectedDeviceId: string;
  connectedDeviceId: string;
  message: string;
  error: string;
};

type AppSettings = {
  name: string;
  age: string;
  language: 'ja' | 'en' | 'zh' | 'ko';
};

type NativeMidiBridge = {
  initialize?: () => unknown;
  listDevices?: () => unknown;
  connect?: (deviceId: string | number) => unknown;
  disconnect?: (deviceId: string | number) => unknown;
  sendMidiMessage?: (deviceId: string | number, messageJson: string) => unknown;
};

type WebMidiMessageEvent = {
  data: Uint8Array | number[];
};

type WebMidiPort = {
  id: string;
  name?: string;
  manufacturer?: string;
  type: 'input' | 'output';
  state?: string;
};

type WebMidiInput = WebMidiPort & {
  onmidimessage: ((event: WebMidiMessageEvent) => void) | null;
};

type WebMidiOutput = WebMidiPort & {
  send: (message: number[]) => void;
};

type WebMidiPortCollection<T> = {
  get: (key: string) => T | undefined;
  forEach: (callback: (port: T) => void) => void;
  values: () => IterableIterator<T>;
};

type WebMidiAccess = {
  inputs: WebMidiPortCollection<WebMidiInput>;
  outputs: WebMidiPortCollection<WebMidiOutput>;
  onstatechange: (() => void) | null;
};

type MidiNavigator = Navigator & {
  requestMIDIAccess?: (options?: { sysex?: boolean }) => Promise<WebMidiAccess>;
};

type MidiWindow = Window & {
  AndroidMidiBridge?: NativeMidiBridge;
  onMidiBridgeReady?: (payload: unknown) => void;
  onMidiMessage?: (payload: unknown) => void;
  onMidiDeviceStateChanged?: (payload: unknown) => void;
  onMidiBridgeError?: (payload: unknown) => void;
};

const defaultSettings: AppSettings = {
  name: '',
  age: '',
  language: 'ja',
};

const siteNav = [
  { label: 'ホーム', href: 'https://kids-piano.com/' },
  { label: '練習動画', href: 'https://kids-piano.com/practice' },
  { label: '教材', href: 'https://kids-piano.com/downloads' },
  { label: 'ゲーム', href: '/games/' },
  { label: '先生ページ', href: 'https://kids-piano.com/teacher' },
  { label: 'お問い合わせ', href: 'https://kids-piano.com/contact' },
];

function currentGameId(): GameId | 'home' {
  const match = window.location.pathname.match(/\/games\/([^/]+)\/?/);
  const id = match?.[1] as GameId | undefined;
  if (id && games.some((game) => game.id === id)) {
    return id;
  }
  return 'home';
}

export function App() {
  const [route, setRoute] = useState<GameId | 'home'>(currentGameId);
  const [surface] = useState<Surface>(detectSurface);
  const [settings, setSettings] = useState<AppSettings>(readSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const onPop = () => setRoute(currentGameId());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.surface = surface;
    document.body.dataset.surface = surface;
  }, [surface]);

  useEffect(() => {
    window.localStorage.setItem('kidsPianoAppSettings', JSON.stringify(settings));
  }, [settings]);

  function navigate(href: string) {
    window.history.pushState({}, '', href);
    setRoute(currentGameId());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const content = route === 'home'
    ? <Home onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
    : route === 'mock-keyboard'
      ? <KeyboardGame meta={games.find((game) => game.id === route) || games[0]} onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
      : <QuizGame gameId={route} meta={games.find((game) => game.id === route) || games[0]} onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />;

  return (
    <AppFrame surface={surface}>
      {content}
      {settingsOpen ? <SettingsModal settings={settings} onClose={() => setSettingsOpen(false)} onSave={setSettings} /> : null}
    </AppFrame>
  );
}

function readSettings(): AppSettings {
  try {
    const parsed = JSON.parse(window.localStorage.getItem('kidsPianoAppSettings') || '{}') as Partial<AppSettings>;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      age: typeof parsed.age === 'string' ? parsed.age : '',
      language: parsed.language === 'en' || parsed.language === 'zh' || parsed.language === 'ko' ? parsed.language : 'ja',
    };
  } catch {
    return defaultSettings;
  }
}

function detectSurface(): Surface {
  const params = new URLSearchParams(window.location.search);
  if (['1', 'true', 'app', 'webview'].includes((params.get('webview') || params.get('app') || params.get('surface') || '').toLowerCase())) {
    return 'webview';
  }
  if (params.get('browser') === '1') {
    return 'browser';
  }
  const ua = navigator.userAgent;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  const isAndroidWebView = /; wv\)/i.test(ua) || /\bVersion\/[\d.]+.*Chrome\/[\d.]+ Mobile Safari\//i.test(ua);
  const isNamedApp = /KidsPianoApp|KidsPianoWebView/i.test(ua);
  return isStandalone || isAndroidWebView || isNamedApp ? 'webview' : 'browser';
}

function AppFrame({ surface, children }: { surface: Surface; children: React.ReactNode }) {
  return (
    <div className={`app-frame surface-${surface}`}>
      {surface === 'browser' ? <BrowserHeader /> : null}
      {children}
    </div>
  );
}

function BrowserHeader() {
  return (
    <header className="site-header">
      <div className="site-header__inner">
        <a className="site-brand" href="https://kids-piano.com/">
          <img src="/assets/brand/kidspiano-app-icon-192.png" alt="" width="42" height="42" />
          <span>Kids Piano</span>
        </a>
        <nav className="site-nav" aria-label="主要ナビゲーション">
          {siteNav.map((item) => (
            <a className={item.label === 'ゲーム' ? 'is-active' : ''} href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

function SettingsModal({ settings, onClose, onSave }: { settings: AppSettings; onClose: () => void; onSave: (settings: AppSettings) => void }) {
  const [draft, setDraft] = useState<AppSettings>(settings);

  function save() {
    onSave(draft);
    onClose();
  }

  return (
    <div className="modal-backdrop settings-backdrop" onClick={onClose}>
      <section className="settings-modal" role="dialog" aria-modal="true" aria-label="アプリ設定" onClick={(event) => event.stopPropagation()}>
        <div className="game-menu-modal__head">
          <h2>設定</h2>
          <button type="button" onClick={onClose}>×</button>
        </div>
        <div className="settings-form">
          <label>
            <span>なまえ</span>
            <input
              type="text"
              inputMode="text"
              maxLength={24}
              value={draft.name}
              placeholder="なまえ"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </label>
          <label>
            <span>ねんれい</span>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              max="99"
              value={draft.age}
              placeholder="6"
              onChange={(event) => setDraft({ ...draft, age: event.target.value })}
            />
          </label>
          <label>
            <span>ことば</span>
            <select value={draft.language} onChange={(event) => setDraft({ ...draft, language: event.target.value as AppSettings['language'] })}>
              <option value="ja">日本語</option>
              <option value="en">English</option>
              <option value="zh">中文</option>
              <option value="ko">한국어</option>
            </select>
          </label>
          <MidiConnectionPanel />
        </div>
        <div className="settings-actions">
          <button type="button" onClick={onClose}>キャンセル</button>
          <button type="button" onClick={save}>保存</button>
        </div>
      </section>
    </div>
  );
}

function MidiConnectionPanel() {
  const [state, setState] = useState<MidiState>(() => ({
    mode: detectMidiMode(),
    status: detectMidiMode() === 'unsupported' ? 'unsupported' : 'idle',
    devices: [],
    selectedDeviceId: '',
    connectedDeviceId: '',
    message: '',
    error: '',
  }));
  const [webMidiAccess, setWebMidiAccess] = useState<WebMidiAccess | null>(null);

  useEffect(() => {
    const midiWindow = window as MidiWindow;
    const previous = {
      onMidiBridgeReady: midiWindow.onMidiBridgeReady,
      onMidiMessage: midiWindow.onMidiMessage,
      onMidiDeviceStateChanged: midiWindow.onMidiDeviceStateChanged,
      onMidiBridgeError: midiWindow.onMidiBridgeError,
    };

    midiWindow.onMidiBridgeReady = () => {
      setState((current) => ({ ...current, mode: 'native', status: current.connectedDeviceId ? 'connected' : 'ready', error: '', message: 'アプリのMIDIブリッジを初期化しました' }));
    };
    midiWindow.onMidiMessage = (payload) => {
      const parsed = parseMidiPayload(payload, null) as { data?: number[] } | null;
      setState((current) => ({ ...current, message: formatMidiMessage(parsed?.data || []), error: '' }));
    };
    midiWindow.onMidiDeviceStateChanged = (payload) => {
      const devices = normalizeNativeMidiDevices(parseMidiPayload(payload, []));
      setState((current) => ({
        ...current,
        devices,
        selectedDeviceId: current.selectedDeviceId && devices.some((device) => device.id === current.selectedDeviceId)
          ? current.selectedDeviceId
          : devices[0]?.id || '',
        connectedDeviceId: devices.find((device) => device.isConnected)?.id || current.connectedDeviceId,
        status: devices.find((device) => device.isConnected) ? 'connected' : 'ready',
        error: '',
      }));
    };
    midiWindow.onMidiBridgeError = (payload) => {
      const parsed = parseMidiPayload(payload, {}) as { message?: string };
      setState((current) => ({ ...current, status: 'error', error: parsed.message || 'MIDIブリッジでエラーが発生しました' }));
    };

    return () => {
      midiWindow.onMidiBridgeReady = previous.onMidiBridgeReady;
      midiWindow.onMidiMessage = previous.onMidiMessage;
      midiWindow.onMidiDeviceStateChanged = previous.onMidiDeviceStateChanged;
      midiWindow.onMidiBridgeError = previous.onMidiBridgeError;
    };
  }, []);

  useEffect(() => {
    if (!webMidiAccess) return;
    const refresh = () => {
      const devices = normalizeWebMidiDevices(webMidiAccess);
      setState((current) => ({
        ...current,
        devices,
        selectedDeviceId: current.selectedDeviceId && devices.some((device) => device.id === current.selectedDeviceId)
          ? current.selectedDeviceId
          : devices[0]?.id || '',
        status: current.connectedDeviceId ? 'connected' : 'ready',
      }));
    };
    webMidiAccess.onstatechange = refresh;
    refresh();
    return () => {
      webMidiAccess.onstatechange = null;
    };
  }, [webMidiAccess]);

  const selectedDevice = state.devices.find((device) => device.id === state.selectedDeviceId) || null;
  const connectedDevice = state.devices.find((device) => device.id === state.connectedDeviceId) || null;

  async function initializeMidi() {
    if (state.mode === 'native') {
      const bridge = (window as MidiWindow).AndroidMidiBridge;
      try {
        bridge?.initialize?.();
        const result = bridge?.listDevices?.();
        if (result) {
          const devices = normalizeNativeMidiDevices(parseMidiPayload(result, []));
          setState((current) => ({
            ...current,
            devices,
            selectedDeviceId: devices[0]?.id || current.selectedDeviceId,
            status: 'ready',
            error: '',
            message: '接続できる電子ピアノを探しました',
          }));
        } else {
          setState((current) => ({ ...current, status: 'ready', error: '', message: '接続できる電子ピアノを探しています' }));
        }
      } catch (error) {
        setState((current) => ({ ...current, status: 'error', error: error instanceof Error ? error.message : 'MIDIブリッジを起動できませんでした' }));
      }
      return;
    }

    const midiNavigator = navigator as MidiNavigator;
    if (!midiNavigator.requestMIDIAccess) {
      setState((current) => ({ ...current, mode: 'unsupported', status: 'unsupported', error: 'このブラウザはWeb MIDIに対応していません' }));
      return;
    }

    try {
      const access = await midiNavigator.requestMIDIAccess() as unknown as WebMidiAccess;
      setWebMidiAccess(access);
      const devices = normalizeWebMidiDevices(access);
      setState((current) => ({
        ...current,
        mode: 'webmidi',
        status: 'ready',
        devices,
        selectedDeviceId: devices[0]?.id || current.selectedDeviceId,
        error: '',
        message: devices.length ? '接続できる電子ピアノを見つけました' : '電子ピアノが見つかりませんでした',
      }));
    } catch {
      setState((current) => ({ ...current, status: 'error', error: 'MIDIの使用が許可されませんでした' }));
    }
  }

  function connectMidi() {
    if (!selectedDevice) {
      setState((current) => ({ ...current, error: '接続する電子ピアノを選んでください' }));
      return;
    }

    if (state.mode === 'native') {
      try {
        (window as MidiWindow).AndroidMidiBridge?.connect?.(selectedDevice.rawId);
        setState((current) => ({ ...current, connectedDeviceId: selectedDevice.id, status: 'connected', error: '', message: `${selectedDevice.name} に接続しました` }));
      } catch (error) {
        setState((current) => ({ ...current, status: 'error', error: error instanceof Error ? error.message : '接続できませんでした' }));
      }
      return;
    }

    if (webMidiAccess) {
      webMidiAccess.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
      const input = webMidiAccess.inputs.get(stripMidiDirection(selectedDevice.id));
      if (input) {
        input.onmidimessage = (event) => {
          setState((current) => ({ ...current, message: formatMidiMessage(Array.from(event.data)), error: '' }));
        };
      }
    }

    setState((current) => ({ ...current, connectedDeviceId: selectedDevice.id, status: 'connected', error: '', message: `${selectedDevice.name} に接続しました` }));
  }

  function disconnectMidi() {
    const device = connectedDevice || selectedDevice;
    if (state.mode === 'native' && device) {
      try {
        (window as MidiWindow).AndroidMidiBridge?.disconnect?.(device.rawId);
      } catch {
        // Native disconnect errors should not strand the UI in connected state.
      }
    }
    if (webMidiAccess) {
      webMidiAccess.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
    }
    setState((current) => ({ ...current, connectedDeviceId: '', status: current.mode === 'unsupported' ? 'unsupported' : 'ready', error: '', message: '接続を解除しました' }));
  }

  function sendTestNote() {
    const device = connectedDevice || selectedDevice;
    if (!device) {
      setState((current) => ({ ...current, error: '先に電子ピアノを接続してください' }));
      return;
    }
    const noteOn = [144, 60, 100];
    const noteOff = [128, 60, 0];

    if (state.mode === 'native') {
      try {
        const bridge = (window as MidiWindow).AndroidMidiBridge;
        bridge?.sendMidiMessage?.(device.rawId, JSON.stringify(noteOn));
        window.setTimeout(() => bridge?.sendMidiMessage?.(device.rawId, JSON.stringify(noteOff)), 240);
        setState((current) => ({ ...current, message: 'テスト音 C4 を送信しました', error: '' }));
      } catch (error) {
        setState((current) => ({ ...current, error: error instanceof Error ? error.message : 'テスト音を送れませんでした' }));
      }
      return;
    }

    const output = webMidiAccess?.outputs.get(stripMidiDirection(device.id));
    if (!output) {
      setState((current) => ({ ...current, error: 'このデバイスにはMIDI出力ポートがありません' }));
      return;
    }
    output.send(noteOn);
    window.setTimeout(() => output.send(noteOff), 240);
    setState((current) => ({ ...current, message: 'テスト音 C4 を送信しました', error: '' }));
  }

  const statusLabel = state.status === 'connected'
    ? '接続中'
    : state.status === 'ready'
      ? '準備OK'
      : state.status === 'unsupported'
        ? '未対応'
        : state.status === 'error'
          ? '確認が必要'
          : '未接続';

  return (
    <section className="midi-settings" aria-label="電子ピアノ接続">
      <div className="midi-settings__head">
        <span>電子ピアノ接続</span>
        <b className={`midi-status midi-status-${state.status}`}>{statusLabel}</b>
      </div>
      <p>{state.mode === 'native' ? 'アプリ内のMIDI接続を使います。' : 'Bluetooth MIDIやUSB MIDI対応の電子ピアノを接続できます。'}</p>
      <div className="midi-controls">
        <button type="button" onClick={initializeMidi}>探す</button>
        <select
          aria-label="MIDIデバイス"
          disabled={!state.devices.length}
          value={state.selectedDeviceId}
          onChange={(event) => setState((current) => ({ ...current, selectedDeviceId: event.target.value }))}
        >
          {state.devices.length ? state.devices.map((device) => (
            <option key={device.id} value={device.id}>{device.name} / {device.type}</option>
          )) : <option value="">デバイスなし</option>}
        </select>
      </div>
      <div className="midi-actions">
        <button type="button" disabled={!selectedDevice || state.status === 'connected'} onClick={connectMidi}>接続</button>
        <button type="button" disabled={state.status !== 'connected'} onClick={disconnectMidi}>切断</button>
        <button type="button" disabled={state.status !== 'connected'} onClick={sendTestNote}>テスト</button>
      </div>
      {state.message ? <small className="midi-message">{state.message}</small> : null}
      {state.error ? <small className="midi-error">{state.error}</small> : null}
    </section>
  );
}

function detectMidiMode(): MidiMode {
  if ((window as MidiWindow).AndroidMidiBridge) {
    return 'native';
  }
  return (navigator as MidiNavigator).requestMIDIAccess ? 'webmidi' : 'unsupported';
}

function parseMidiPayload(payload: unknown, fallbackValue: unknown) {
  if (payload == null || payload === '') return fallbackValue;
  if (typeof payload === 'object') return payload;
  if (typeof payload !== 'string') return fallbackValue;
  try {
    return JSON.parse(payload);
  } catch {
    return fallbackValue;
  }
}

function normalizeNativeMidiDevices(input: unknown): MidiDevice[] {
  if (!Array.isArray(input)) return [];
  return input.map((device, index) => {
    const item = device as Partial<MidiDevice> & { deviceId?: string | number };
    const rawId = item.rawId ?? item.id ?? item.deviceId ?? index + 1;
    return {
      id: String(rawId),
      rawId,
      name: item.name ? String(item.name) : `MIDI Device ${index + 1}`,
      type: item.type ? String(item.type) : 'midi',
      inputPorts: Number(item.inputPorts || 0),
      outputPorts: Number(item.outputPorts || 0),
      isConnected: Boolean(item.isConnected),
    };
  });
}

function normalizeWebMidiDevices(access: WebMidiAccess): MidiDevice[] {
  const inputs = Array.from(access.inputs.values()).map((input) => webMidiPortToDevice(input));
  const outputs = Array.from(access.outputs.values()).map((output) => webMidiPortToDevice(output));
  return [...inputs, ...outputs];
}

function webMidiPortToDevice(port: WebMidiPort): MidiDevice {
  return {
    id: `${port.type}:${port.id}`,
    rawId: port.id,
    name: port.name || port.manufacturer || 'MIDI Device',
    type: port.type === 'input' ? '入力' : '出力',
    inputPorts: port.type === 'input' ? 1 : 0,
    outputPorts: port.type === 'output' ? 1 : 0,
    isConnected: port.state === 'connected',
  };
}

function stripMidiDirection(deviceId: string) {
  return deviceId.replace(/^(input|output):/, '');
}

function formatMidiMessage(data: number[]) {
  if (!data.length) return 'MIDIメッセージを受信しました';
  const [status, note, velocity] = data;
  const command = status >= 144 && status <= 159 && velocity > 0 ? 'Note On' : status >= 128 && status <= 143 ? 'Note Off' : 'MIDI';
  return `${command}: ${note ?? '-'} / ${velocity ?? '-'}`;
}

function Home({ onNavigate, onOpenSettings }: { onNavigate: (href: string) => void; onOpenSettings: () => void }) {
  return (
    <main className="app-home">
      <section className="app-hero">
        <div>
          <p className="eyebrow">Kids Piano App</p>
          <h1>音をきいて、えらぶだけ。</h1>
          <p>楽しくクイズにチャレンジして、音感・読譜・リズム・鍵盤を育てよう。</p>
          <div className="hero-actions">
            <button type="button" onClick={() => onNavigate('/games/ear-quiz/')}>音当てクイズ</button>
            <button type="button" onClick={() => onNavigate('/games/note-quiz/')}>音符クイズ</button>
          </div>
        </div>
        <div className="hero-preview" aria-hidden="true">
          <div className="hero-preview__screen">
            <div className="mini-top"><span>音当てクイズ</span><b>3/10</b></div>
            <div className="mini-play">♪</div>
            <div className="mini-cards">
              <span>C</span><span>D</span><span className="is-hit">E</span><span>F</span><span>G</span>
            </div>
          </div>
        </div>
      </section>
      <section className="game-grid" aria-label="ゲーム一覧">
        {games.map((game) => (
          <button className={`game-card tone-${game.color}`} key={game.id} type="button" onClick={() => onNavigate(`/games/${game.id}/`)}>
            <span>{game.skill}</span>
            <strong>{game.title}</strong>
            <small>{game.lead}</small>
          </button>
        ))}
      </section>
      <HomeDock onNavigate={onNavigate} onOpenSettings={onOpenSettings} />
    </main>
  );
}

function HomeDock({ onNavigate, onOpenSettings }: { onNavigate: (href: string) => void; onOpenSettings: () => void }) {
  return (
    <nav className="app-dock home-dock" aria-label="アプリホームメニュー">
      <button type="button" onClick={() => window.location.href = 'https://kids-piano.com/'}>
        <span aria-hidden="true">⌂</span>
        <b>サイト</b>
      </button>
      <button type="button" onClick={() => onNavigate('/games/ear-quiz/')}>
        <span aria-hidden="true">▶</span>
        <b>はじめる</b>
      </button>
      <button type="button" onClick={() => onNavigate('/games/mock-keyboard/')}>
        <span aria-hidden="true">♪</span>
        <b>けんばん</b>
      </button>
      <button type="button" onClick={onOpenSettings}>
        <span aria-hidden="true">⚙</span>
        <b>設定</b>
      </button>
    </nav>
  );
}

function GameShell({ meta, score, children, dockActions, onNavigate, onOpenSettings }: { meta: GameMeta; score?: Score; children: React.ReactNode; dockActions?: React.ReactNode; onNavigate: (href: string) => void; onOpenSettings: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hearts = Math.max(0, 3 - ((score?.total || 0) - (score?.correct || 0)));

  function switchGame(href: string) {
    setMenuOpen(false);
    onNavigate(href);
  }

  return (
    <main className={`game-shell tone-${meta.color}`}>
      <nav className="game-topbar" aria-label="ゲームメニュー">
        <div className="game-titlemark">
          <span className="game-titlemark__icon" aria-hidden="true">♪</span>
          <div>
            <h1 className="game-titlemark__title">{meta.title}</h1>
            <span>{meta.skill}</span>
          </div>
        </div>
      </nav>
      <header className="game-header">
        <div>
          <p className="eyebrow">{meta.shortTitle}</p>
          <h1>{meta.title}</h1>
          <p>{meta.lead}</p>
        </div>
        {score ? (
          <div className="game-stats">
            <div className="progress-line"><span style={{ width: `${Math.min(score.total, 10) * 10}%` }} /></div>
            <div className="score-pills">
              <span>問題 <b>{score.total}</b> / 10</span>
              <span>COMBO <b>{score.combo}</b></span>
              <span>スコア <b>{score.correct * 250 + score.bestCombo * 50}</b></span>
              <span className="hearts">{Array.from({ length: 3 }, (_, index) => index < hearts ? '♥' : '♡').join(' ')}</span>
            </div>
          </div>
        ) : null}
      </header>
      {children}
      <GameSwitch current={meta.id} onNavigate={onNavigate} />
      <nav className="app-dock" aria-label="アプリメニュー">
        <button type="button" onClick={() => onNavigate('/games/')}>
          <span aria-hidden="true">⌂</span>
          <b>ホーム</b>
        </button>
        <button type="button" onClick={() => setMenuOpen(true)}>
          <span aria-hidden="true">↔</span>
          <b>切替</b>
        </button>
        <button type="button" onClick={onOpenSettings}>
          <span aria-hidden="true">⚙</span>
          <b>設定</b>
        </button>
        {dockActions}
      </nav>
      {menuOpen ? <GameMenuModal current={meta.id} onClose={() => setMenuOpen(false)} onNavigate={switchGame} /> : null}
    </main>
  );
}

function GameMenuModal({ current, onClose, onNavigate }: { current: GameId; onClose: () => void; onNavigate: (href: string) => void }) {
  return (
    <div className="modal-backdrop game-menu-backdrop" onClick={onClose}>
      <section className="game-menu-modal" role="dialog" aria-modal="true" aria-label="ゲーム切替" onClick={(event) => event.stopPropagation()}>
        <div className="game-menu-modal__head">
          <h2>ゲームを選ぶ</h2>
          <button type="button" onClick={onClose}>×</button>
        </div>
        <div className="game-menu-list">
          {games.map((game) => (
            <button
              className={`tone-${game.color} ${game.id === current ? 'is-active' : ''}`}
              key={game.id}
              type="button"
              onClick={() => onNavigate(`/games/${game.id}/`)}
            >
              <span>{game.shortTitle}</span>
              <strong>{game.title}</strong>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function GameSwitch({ current, onNavigate }: { current: GameId; onNavigate: (href: string) => void }) {
  return (
    <section className="switcher" aria-label="別のゲーム">
      {games.map((game) => (
        <button className={game.id === current ? 'is-active' : ''} key={game.id} type="button" onClick={() => onNavigate(`/games/${game.id}/`)}>
          {game.shortTitle}
        </button>
      ))}
    </section>
  );
}

function QuizGame({ gameId, meta, onNavigate, onOpenSettings }: { gameId: GameId; meta: GameMeta; onNavigate: (href: string) => void; onOpenSettings: () => void }) {
  const [level, setLevel] = useState<GameLevel>(1);
  const [levelOpen, setLevelOpen] = useState(false);
  const [score, setScore] = useState<Score>({ total: 0, correct: 0, combo: 0, bestCombo: 0 });
  const [question, setQuestion] = useState<QuizQuestion>(() => makeQuestion(gameId, level));
  const [result, setResult] = useState<ResultState>(null);

  useEffect(() => {
    setScore({ total: 0, correct: 0, combo: 0, bestCombo: 0 });
    setQuestion(makeQuestion(gameId, level));
    setResult(null);
  }, [gameId, level]);

  function next() {
    if (score.total >= 10) {
      setScore({ total: 0, correct: 0, combo: 0, bestCombo: 0 });
    }
    setQuestion(makeQuestion(gameId, level));
    setResult(null);
  }

  async function replay() {
    if (gameId === 'ear-quiz' && question.note) {
      await playNote(question.note);
    }
    if (gameId === 'rhythm-quiz' && question.rhythm) {
      await playRhythm(question.rhythm);
    }
  }

  function answer(value: string) {
    const ok = value === question.answer;
    playFeedback(ok);
    const combo = ok ? score.combo + 1 : 0;
    const nextScore = {
      total: score.total + 1,
      correct: score.correct + (ok ? 1 : 0),
      combo,
      bestCombo: Math.max(score.bestCombo, combo),
    };
    setScore(nextScore);
    setResult({
      ok,
      title: ok ? 'せいかい！' : 'もう一回いける！',
      detail: gameId === 'rhythm-quiz' ? '正解のリズムを確認しよう' : `正解: ${question.answer}`,
    });
  }

  const complete = score.total >= 10;

  return (
    <GameShell meta={meta} score={score} dockActions={<LevelSelector value={level} onOpen={() => setLevelOpen(true)} />} onNavigate={onNavigate} onOpenSettings={onOpenSettings}>
      <section className="play-panel">
        <QuestionView gameId={gameId} question={question} onReplay={replay} />
        <div className="choice-grid">
          {question.choices.map((choice) => (
            <ChoiceButton
              choice={choice}
              disabled={Boolean(result)}
              gameId={gameId}
              isCorrect={Boolean(result) && choice === question.answer}
              isWrong={Boolean(result) && choice !== question.answer}
              key={choice}
              onClick={() => answer(choice)}
            />
          ))}
        </div>
      </section>
      {levelOpen ? (
        <LevelModal
          value={level}
          onClose={() => setLevelOpen(false)}
          onChange={(nextLevel) => {
            setLevel(nextLevel);
            setLevelOpen(false);
          }}
        />
      ) : null}
      {result ? <ResultModal result={result} complete={complete} score={score} onNext={next} /> : null}
    </GameShell>
  );
}

function LevelSelector({ value, onOpen }: { value: GameLevel; onOpen: () => void }) {
  return (
    <button className="level-trigger" type="button" onClick={onOpen}>
      <span aria-hidden="true">Lv</span>
      <b>{value}</b>
    </button>
  );
}

function LevelModal({ value, onClose, onChange }: { value: GameLevel; onClose: () => void; onChange: (level: GameLevel) => void }) {
  return (
    <div className="modal-backdrop level-modal-backdrop" onClick={onClose}>
      <section className="level-modal" role="dialog" aria-modal="true" aria-label="難易度選択" onClick={(event) => event.stopPropagation()}>
        <div className="game-menu-modal__head">
          <h2>難易度を選ぶ</h2>
          <button type="button" onClick={onClose}>×</button>
        </div>
        <div className="level-modal-list">
          {gameLevels.map((level) => (
            <button
              className={value === level ? 'is-active' : ''}
              key={level}
              type="button"
              onClick={() => onChange(level)}
            >
              <span>Level</span>
              <strong>{level}</strong>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function ChoiceButton({ choice, disabled, gameId, isCorrect, isWrong, onClick }: { choice: string; disabled: boolean; gameId: GameId; isCorrect: boolean; isWrong: boolean; onClick: () => void }) {
  const note = choice.match(/\(([A-G][0-9])\)/)?.[1];
  const rhythm = gameId === 'rhythm-quiz' ? choice.match(/^パターン (L[1-4][A-D])$/)?.[1] : '';
  const image = note && gameId === 'ear-quiz'
    ? noteImagePath(note)
    : rhythm
      ? `/shared/media/test003/${rhythm}.png`
      : '';
  const noteLabel = note ? labelForNote(note).split(' ')[0] : choice;
  const tone = note ? note.charAt(0).toLowerCase() : '';
  const label = rhythm ? 'リズム' : choice;
  const classes = [
    image ? 'has-choice-image' : '',
    rhythm ? 'has-rhythm-image' : '',
    tone ? `note-tone-${tone}` : '',
    isCorrect ? 'is-correct' : '',
    isWrong ? 'is-dimmed' : '',
  ].filter(Boolean).join(' ');
  return (
    <button className={classes} disabled={disabled} type="button" onClick={onClick}>
      {image ? <img src={image} alt="" loading="lazy" /> : null}
      {image && !rhythm ? null : <span>{label}</span>}
      {image && !rhythm ? <b>{noteLabel}</b> : null}
    </button>
  );
}

function QuestionView({ gameId, question, onReplay }: { gameId: GameId; question: QuizQuestion; onReplay: () => void }) {
  return (
    <div className="question-view">
      <div className="floating-notes" aria-hidden="true"><span>♪</span><span>♫</span><span>♬</span></div>
      <p className="prompt">{question.prompt}</p>
      {gameId === 'ear-quiz' || gameId === 'rhythm-quiz' ? (
        <button className="listen-button" type="button" onClick={onReplay}>
          <span aria-hidden="true">♬</span>
          <strong>きく</strong>
        </button>
      ) : null}
      {question.image && gameId !== 'rhythm-quiz' ? <img className="note-card-image" src={question.image} alt="音符カード" /> : null}
      {question.rhythm && gameId !== 'rhythm-quiz' ? <RhythmCard pattern={question.rhythm} /> : null}
      {question.chord ? <ChordCard notes={question.chord} /> : null}
      {question.keySignature ? <KeySignatureCard value={question.keySignature} /> : null}
      {question.subtitle ? <p className="question-subtitle">{question.subtitle}</p> : null}
    </div>
  );
}

function ResultModal({ result, complete, score, onNext }: { result: NonNullable<ResultState>; complete: boolean; score: Score; onNext: () => void }) {
  const stars = score.correct >= 9 ? 3 : score.correct >= 6 ? 2 : score.correct >= 3 ? 1 : 0;
  return (
    <div className="modal-backdrop">
      <section className="result-modal" role="dialog" aria-modal="true">
        <p className={result.ok ? 'answer-mark ok' : 'answer-mark'}>{result.ok ? '○' : '×'}</p>
        <h2>{complete ? '10もんのけっか' : result.title}</h2>
        {complete ? <div className="stars">{Array.from({ length: 3 }, (_, index) => index < stars ? '★' : '☆').join('')}</div> : null}
        <p>{complete ? `${score.correct} / 10 正解・最大COMBO ${score.bestCombo}` : result.detail}</p>
        <button type="button" onClick={onNext}>{complete ? 'もう一回' : 'つぎへ'}</button>
      </section>
    </div>
  );
}

function KeyboardGame({ meta, onNavigate, onOpenSettings }: { meta: GameMeta; onNavigate: (href: string) => void; onOpenSettings: () => void }) {
  const [last, setLast] = useState<NoteName | null>(null);

  async function press(note: NoteName) {
    setLast(note);
    await playNote(note);
  }

  return (
    <GameShell meta={meta} onNavigate={onNavigate} onOpenSettings={onOpenSettings}>
      <section className="keyboard-panel">
        <div className="note-display">
          <strong>{last ? labelForNote(last) : '鍵盤を押してね'}</strong>
          <span>{last ? '音名と場所を確認中' : '白鍵をタップすると音が鳴ります'}</span>
        </div>
        <div className="keyboard">
          {keyboardNotes.map((note) => (
            <button className="white-key" key={note} type="button" onClick={() => press(note)}>
              <span>{note}</span>
              <small>{labelForNote(note).split(' ')[0]}</small>
            </button>
          ))}
        </div>
      </section>
    </GameShell>
  );
}

function RhythmCard({ pattern }: { pattern: string[] }) {
  return (
    <div className="rhythm-card">
      {pattern.map((beat, index) => <span key={`${beat}-${index}`}>{beat}</span>)}
    </div>
  );
}

function ChordCard({ notes }: { notes: string[] }) {
  return (
    <div className="music-card">
      <div className="staff" />
      <div className="chord-notes">
        {notes.map((note) => <span key={note}>{note}</span>)}
      </div>
    </div>
  );
}

function KeySignatureCard({ value }: { value: string }) {
  return (
    <div className="music-card">
      <div className="staff" />
      <strong className="key-signature">{value}</strong>
    </div>
  );
}

function notePoolForLevel(level: GameLevel) {
  if (level === 1) return whiteNotes.slice(0, 5);
  if (level === 2) return whiteNotes.slice(0, 8);
  if (level === 3) return whiteNotes.slice(0, 10);
  return whiteNotes;
}

function levelPool(items: QuizQuestion[], level: GameLevel) {
  const pool = items.filter((item) => !item.levels || item.levels.includes(level));
  return pool.length ? pool : items;
}

function answerChoices(answer: string, answers: string[], count = 4) {
  const wrong = shuffle(Array.from(new Set(answers.filter((item) => item !== answer))));
  return shuffle([answer, ...wrong.slice(0, count - 1)]);
}

function makeQuestion(gameId: GameId, level: GameLevel): QuizQuestion {
  if (gameId === 'ear-quiz') {
    const notes = notePoolForLevel(level);
    const note = pick(notes);
    const answer = labelForNote(note);
    const choices = answerChoices(answer, notes.map(labelForNote), 5);
    return { id: `${note}-${level}`, note, answer, choices, prompt: 'どの音が聞こえたかな？', subtitle: `レベル${level}の音を聞き分けよう` };
  }

  if (gameId === 'note-quiz') {
    const notes = notePoolForLevel(level);
    const note = pick(notes);
    const answer = labelForNote(note);
    const choices = answerChoices(answer, notes.map(labelForNote), 5);
    return { id: `${note}-${level}`, answer, choices, prompt: 'この音符はどれ？', image: noteImagePath(note), subtitle: `レベル${level}の譜面を読もう` };
  }

  if (gameId === 'rhythm-quiz') {
    const pool = levelPool(rhythmPatterns, level);
    const question = pick(pool);
    return { ...question, choices: answerChoices(question.answer, pool.map((item) => item.answer)) };
  }

  if (gameId === 'chord-quiz') {
    const pool = levelPool(chordQuestions, level);
    const question = pick(pool);
    return { ...question, choices: answerChoices(question.answer, pool.map((item) => item.answer)) };
  }

  const pool = levelPool(keySignatureQuestions, level);
  const question = pick(pool);
  return { ...question, choices: answerChoices(question.answer, pool.map((item) => item.answer)) };
}

async function playNote(note: string) {
  const audio = new Audio(noteAudioPath(note));
  audio.volume = 0.9;
  await audio.play();
}

function playFeedback(ok: boolean) {
  const audio = new Audio(ok ? '/shared/sfx/quiz-correct.mp3' : '/shared/sfx/quiz-incorrect.mp3');
  audio.volume = 0.9;
  audio.play().catch(() => {});
}

async function playRhythm(pattern: string[]) {
  const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return;
  const ctx = new AudioContextClass();
  const now = ctx.currentTime + 0.05;
  let cursor = now;
  pattern.forEach((symbol) => {
    if (symbol !== '𝄽') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.001, cursor);
      gain.gain.exponentialRampToValueAtTime(0.18, cursor + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, cursor + 0.13);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(cursor);
      osc.stop(cursor + 0.14);
    }
    cursor += symbol === '♫' ? 0.28 : 0.42;
  });
}
